import axios from "axios";

export const TAX_TYPE = {
    PERCENTAGE: 0,
    CURRENCY: 1
};
function defaultState() {
    return {
        isLoading: true,
        isLoadingItems: true,
        isSavingEdit: false,
        details: {},
        items: [],
        receiptId: null,
        receiptIndex: null,
        isEditMode: false,
        editDetails: {},
        isCustomTaxItem: false // If false, then existing receipt before VET-1883
    };
}

function loading(commit, key, bool) {
    commit("setLoader", { key: key, value: bool });
}

function properNumber(raw) {
    if (raw) {
        let n = Number(raw);

        if (isNaN(n)) {
            return 0;
        }

        return n;
    } else {
        return 0;
    }
}

function calculateTotal({ cost, tax, shipping }) {
    // convert to number
    let nCost = cost ? Number(cost) : 0;
    let nTax = tax ? Number(tax) : 0;
    let nShipping = shipping ? Number(shipping) : 0;

    if (isNaN(nCost)) {
        nCost = 0;
    }

    if (isNaN(nTax)) {
        nTax = 0;
    }

    if (isNaN(nShipping)) {
        nShipping = 0;
    }

    return nCost + nTax + nShipping;
}

const state = defaultState();

const actions = {
    async fetchItems({ commit }, payload) {
        try {
            loading(commit, "isLoadingItems", true);

            let res = await axios.get(
                `${process.env.BARRAMUNDI}inventory/receipt/items/${payload.receiptId}`
            );

            let response = res.data.response;
            commit("setItems", response.resources || []);
            loading(commit, "isLoadingItems", false);
        } catch (error) {
            loading(commit, "isLoadingItems", false);
            throw new Error("Unable to fetch items.");
        }
    },
    async fetchDetails({ commit }, payload) {
        try {
            loading(commit, "isLoading", true);

            let res = await axios.get(
                `${process.env.BARRAMUNDI}inventory/receipt/${payload.receiptId}`
            );

            let response = res.data.response;

            if (response && response.resources) {
                let resources = response.resources;
                let supplier = resources.supplier || {};
                let statusLabel = resources.status.label;

                let total = calculateTotal({
                    cost: resources.amount,
                    shipping: resources.shipping,
                    tax: resources.tax
                });

                commit("setDetails", {
                    supplier: supplier.name || "-",
                    supplierEncodedId: supplier.encoded_id,
                    invoiceNumber: resources.invoice_number,
                    date: resources.date,
                    status: resources.status,
                    cost: properNumber(resources.amount),
                    shipping: properNumber(resources.shipping),
                    tax: properNumber(resources.tax),
                    taxPercentage: properNumber(resources.tax_percentage),
                    total: properNumber(total),
                    notes: resources.notes,
                    statusLabel: statusLabel,
                    postDate: resources.post_at || null
                });

                commit("setIndex", resources.index);
                commit(
                    "setIsCustomTaxItem",
                    resources.tax_type == TAX_TYPE.PERCENTAGE ||
                        resources.show_tax
                );
                commit("setId", resources.id);

                if (payload.autoEdit) {
                    commit("initEditOrder");
                }
            }

            loading(commit, "isLoading", false);
        } catch (error) {
            loading(commit, "isLoading", false);
            throw new Error("Unable to fetch receipt details.");
        }
    },
    async editOrder({ commit }) {
        commit("initEditOrder");
    },
    async saveEdit({ state, commit }, payload) {
        loading(commit, "isSavingEdit", true);

        let {
            tax,
            shipping,
            invoiceNumber,
            supplier,
            notes
        } = state.editDetails;
        let isSupplierChanged = typeof supplier === "object";
        let data = {
            date: payload.dateUnix,
            tax: tax,
            shipping: shipping,
            invoice_number: invoiceNumber,
            notes: notes
        };

        if (isSupplierChanged) {
            data.supplier_id = supplier.value;
        }

        let res = await axios.put(
            `${process.env.BARRAMUNDI}inventory/receipt/${state.receiptId}`,
            data
        );

        if (
            res.data.response &&
            res.data.response.message &&
            res.data.response.message.code === 200
        ) {
            let resources = res.data.response.resources;

            let total = calculateTotal({
                cost: resources.amount,
                shipping: resources.shipping,
                tax: resources.tax
            });

            let newDetails = {
                invoiceNumber: resources.invoice_number,
                date: resources.date,
                shipping: properNumber(resources.shipping),
                tax: properNumber(resources.tax),
                total: properNumber(total),
                notes: resources.notes
            };

            if (isSupplierChanged) {
                newDetails.supplierEncodedId = "#"; // <--- backend should return encodedId
                newDetails.supplier = supplier.label;
            }

            commit("updateDetails", newDetails);
        } else {
            throw new Error("Unable to update receipt details.");
        }

        loading(commit, "isSavingEdit", false);
    },
    statusPost({ state, commit }, payload) {
        let currentDetails = state.details;

        commit("setDetails", {
            ...currentDetails,
            status: {
                id: 1,
                label: "Posted"
            },
            statusLabel: "Posted",
            postDate: payload.post_at || null
        });
    }
};

const mutations = {
    setLoader(state, { key, value }) {
        state[key] = value;
    },
    setDetails(state, value) {
        state.details = value;
    },
    setIsCustomTaxItem(state, value) {
        state.isCustomTaxItem = value;
    },
    setItems(state, value) {
        state.items = value;
    },
    setIndex(state, value) {
        state.receiptIndex = value;
    },
    setId(state, value) {
        state.receiptId = value;
    },
    initEditOrder(state) {
        state.isEditMode = true;
        state.editDetails = { ...state.details }; // clone
    },
    updateDetails(state, payload = {}) {
        let newDetails = {
            ...state.details,
            ...payload
        };

        state.details = newDetails;
        state.isEditMode = false;
    },
    setEditDetails(state, value) {
        state.editDetails = value;
    },
    cancelEdit(state) {
        state.isEditMode = false;
        state.editDetails = {};
    },
    setEditDetailsKey(state, { key, value }) {
        state.editDetails[key] = value;
    }
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
    TAX_TYPE
};
